import usePagination from "./hooks/usePagination";
import capitalizeFirstLetter from "./utils/CapitalizeFirstLetter";
import Row from "./Row/Row";
import "./Table.css";
import IconButton from "../UI/Button/IconButton/IconButton";
import InputSearch from "../UI/InputSearch/InputSearch";
import DropDown from "../UI/DropDown/DropDown";
import detectZoom from "/src/lib/detect-zoom/detect-zoom";
import { useState, useEffect, useRef } from "react";
import { upperFirst } from "lodash";
import ProgressBarTable from "./ProgressBarTable/ProgressBarTable";

const zoomLevels = {
    25: 620,
    33: 580,
    50: 520,
    67: 500,
    75: 460,
    80: 420,
    90: 380,
    100: 340,
    110: 300,
    125: 260,
    150: 220,
};

function Table({ className = "table", cells, name, api, actions = [], actionExtension, buttons, filters = [], filterValues, filterPostRequest = (value) => value, filterButton, refreshState = [], plurial, withPagination = true, defaultSort }) {

    const lastScroll = useRef({ top: 0, left: 0 });
    const [data, totalNbData, { addPage, setFilter, setFiltersAll }, rowActions, fetching, isSorted, setIsSorted] = usePagination(api, filterPostRequest, refreshState, filterValues, withPagination, defaultSort);
    const [maxHeight, setMaxHeight] = useState(0);
    const [width, setWidth] = useState(0);
    const [defaultWidth, setDefaultWidth] = useState(0);
    const [zoomFactor, setZoomFactor] = useState(detectZoom.zoom());
    const [height, setHeight] = useState(100);
    const sortAsc = (name) => {
        setIsSorted({ column: name, val: !isSorted.val });
    };

    const refScroll = useRef();
    const refDataContainer = useRef();

    const onScroll = (e) => {
        const el = e.target;

        const deltaY = el.scrollTop - lastScroll.current.top;
        const deltaX = el.scrollLeft - lastScroll.current.left;

        lastScroll.current = {
            top: el.scrollTop,
            left: el.scrollLeft,
        };

        // Scroll horizontal ou vers le haut → on ignore
        if (Math.abs(deltaX) > Math.abs(deltaY) || deltaY <= 0) {
            return;
        }

        const { scrollHeight, clientHeight, scrollTop } = el;

        if (scrollHeight - scrollTop - clientHeight > 40 || withPagination === false) {
            return;
        }

        addPage();
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        setFilter({ [name]: value });
    };

const createColumns = () => {
    const columns = cells.map((cell, index) => (
        <th key={"th" + index} style={{ width: cell.width }}>
            {cell.column}
            {cell.sort ? (
                <i
                    className={
                        "fas " +
                        (isSorted?.val ? "fa-sort-alpha-up" : "fa-sort-alpha-down") +
                        " sort_icon"
                    }
                    onClick={() => sortAsc(cell.name)}
                ></i>
            ) : null}
        </th>
    ));

    if (actions.length > 0) {
        columns.push(
            <th key="th-action" style={{ width: "120px" }}>
                {actionExtension ? actionExtension : "Actions"}
            </th>
        );
    }

    return columns;
};

    const createButtons = () => {
        if (!buttons) {
            return null;
        }

        const buttonsComponent = buttons.map((button, index) => {
            const { onClick } = { ...button };
            const click = onClick.bind(null, { ...rowActions });
            return (
                <IconButton className="main-iconButton profil_action main_add-button space_tableButton" {...button} onClick={click} key={"button-" + index} />
            );
        });

        return buttonsComponent;
    };

    const setMaxHeightWithFactor = () => {
        const tableContainer = document.querySelector(".background-table").offsetHeight;
        const zoomLevel = (detectZoom.zoom() * 100).toFixed(0);
        const constantZoom = zoomLevels[zoomLevel];
        var newMaxHeight = tableContainer - constantZoom * detectZoom.zoom();
        setMaxHeight(newMaxHeight);

        var zoomDiff = zoomFactor - detectZoom.device();
        const zoomValue = 1 + zoomDiff;
        setWidth(defaultWidth * zoomValue);
    };

    useEffect(() => {
        if (refScroll.current == undefined) {
            return;
        }

        setMaxHeightWithFactor();
        refScroll.current.addEventListener("scroll", onScroll);

        return () => {
            refScroll.current?.removeEventListener("scroll", onScroll);
        };
    }, [fetching, height]);

    useEffect(() => {
        window.addEventListener("resize", setMaxHeightWithFactor);
        return () => {
            window.removeEventListener("resize", setMaxHeightWithFactor);
        };
    }, [defaultWidth, height]);

    useEffect(() => {
        if (filterValues == undefined) {
            return;
        }
        setFiltersAll(filterValues);
    }, [filterValues]);

    useEffect(() => {
        if (refDataContainer.current == undefined) {
            return;
        }

        if (refDataContainer.current.firstChild.offsetHeight != 0) {
            setHeight(refDataContainer.current.firstChild.offsetHeight);
        }
        if (refDataContainer.current.firstChild.offsetWidth != 0) {
            setDefaultWidth(50000);
        }
    }, [refDataContainer, data]);

    if (height > maxHeight) {
        className = `scroll ${className}`;
    }

    return (
        <div onScroll={onScroll} style={{ position: "relative" }}>
            <div className="search-container">
                <div className="start-row">
                    <div className="input-container no-margin" style={{ justifyContent: "center" }}>
                        {filters.map((filter, index) => {
                            if (filter.type == "input") {
                                return (
                                    <InputSearch style={{ marginRight: 20 }} key={index} placeholderLibelle={filter.placeholder} name={filter.name} onChange={onChange} />
                                );
                            }

                            if (filter.type == "dropdown") {
                                return (
                                    <div className="dropdown-filter" key={index}>
                                        <label>{upperFirst(filter.name)}</label>
                                        <DropDown {...filter} setValue={(name, value) => onChange({ target: { name, value } })} />
                                    </div>
                                );
                            }
                        })}
                    </div>
                </div>
                <div className="end-row">
                    {createButtons()}
                    {filterButton && <div style={{ marginRight: "5px" }}>{filterButton}</div>}
                </div>
            </div>

            <table className="table-data" style={{ visibility: "hidden", position: "absolute" }} ref={refDataContainer}>
                <tfoot>
                    {data &&
                        data.map((row) => (
                            <Row key={new Date().getTime() + "_" + Math.floor(Math.random() * 100000) + row.id} info={row} cells={cells} actions={actions} functions={rowActions} />
                        ))}
                </tfoot>
            </table>
            <table cellSpacing="0" className={className} ref={refScroll} style={{ maxHeight, height }}>
                { /* <ProgressBarTable isFound={!fetching} />*/}
                <caption className="table-info">
                    <span>
                        List of {capitalizeFirstLetter(name) + (plurial === false ? "" : "s")}{" "}
                        <i className="fas fa-arrow-right arrow_icon_table" /> {data.length} / {totalNbData > data.length ? totalNbData : data.length}
                    </span>
                </caption>
                <thead>
                    <tr>{createColumns(width)}</tr>
                </thead>
                <tbody>
                    {data &&
                        data.map((row) => (
                            <Row key={new Date().getTime() + "_" + Math.floor(Math.random() * 100000) + row.id} info={row} cells={cells} actions={actions} functions={rowActions} width={width} />
                        ))}
                </tbody>
            </table>
        </div>
    );
}

export default Table;
